// ── DASH.B1 — Zone 2: Action Hub ────────────────────────────────────────────
//
// Vertical stack of role-filtered cards (design doc §5.3). Each card COLLAPSES
// when it has nothing to show (§11.4) — it is simply not rendered, rather than
// showing a per-card placeholder. When EVERY configured card is empty, the zone
// shows a single "You're all caught up" message.
//
// The five §5.3 cards are co-located here as internal components for B1's
// layout shell; DASH.B2 (#824) owns enriching them (timing badges, agent-launch
// CTAs, staleness rules, etc.). Cards whose data source does not exist in the
// /overview payload yet (MyAssignedWork) are wired but always collapse.

import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Zone2CardId } from '../../config/dashboardRoleConfig';
import type {
  AssignedWorkItem,
  DashboardOverview,
  NotificationSummaryItem,
  StuckItem,
  UpcomingGate,
} from '../../types/dashboard';
import { formatTimeAgo } from '../../lib/dashboardFormat';
import { TimingBadge } from '../StoryStatusCard';

// No dedicated notifications PAGE route exists yet (only the bell dropdown + the
// notifications API). A live "View all" link would render a blank unmatched
// route (404), so it is gated off until that route lands — the same approach as
// Zone 3's activity link. The card still shows the latest notifications inline.
// TODO(notifications page route): set NOTIFICATIONS_PAGE_PATH (e.g. '/notifications').
const NOTIFICATIONS_PAGE_PATH: string | null = null;

// ── Shared card chrome (§11: pad 24, full left-col width, gap 16) ────────────

const cardStyle: CSSProperties = {
  padding: 24,
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  boxShadow: 'var(--shadow-sm)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const rowLinkStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '8px 0',
  borderTop: '1px solid var(--border)',
  color: 'var(--text)',
  textDecoration: 'none',
  fontSize: 14,
};

function CardShell({
  testid,
  title,
  count,
  accent,
  children,
  footer,
}: {
  testid: string;
  title: string;
  count?: number;
  accent?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section
      data-testid={testid}
      style={{ ...cardStyle, borderLeft: accent ? `3px solid ${accent}` : cardStyle.border }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-display)', margin: 0 }}>
          {title}
        </h2>
        {typeof count === 'number' && count > 0 && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              minWidth: 20,
              textAlign: 'center',
              padding: '1px 7px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--surface-alt)',
              color: 'var(--text-secondary)',
            }}
          >
            {count}
          </span>
        )}
      </header>
      <div style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>
      {footer}
    </section>
  );
}

// ── §5.3 cards ───────────────────────────────────────────────────────────────

function PendingApprovalsCard({ items }: { items: UpcomingGate[] }) {
  return (
    <CardShell testid="card-pending-approvals" title="Pending approvals" count={items.length} accent="var(--primary)">
      {items.slice(0, 5).map((gate, i) => (
        <Link key={`${gate.projectId}-${gate.gateType}-${i}`} to={`/projects/${gate.projectId}`} style={rowLinkStyle}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {gate.context} · {gate.projectName}
          </span>
          <span style={{ color: 'var(--text-secondary)', fontSize: 12, flexShrink: 0 }}>Review →</span>
        </Link>
      ))}
    </CardShell>
  );
}

// Small type tag standing in for the §5.3 "type icon" — a text chip is
// clearer and more robust than an emoji glyph across platforms/tests.
const typeTagStyle: CSSProperties = {
  display: 'inline-block',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.3,
  padding: '1px 5px',
  marginRight: 8,
  borderRadius: 4,
  background: 'var(--surface-alt)',
  color: 'var(--text-secondary)',
  verticalAlign: 'middle',
};

// A normalized "needs attention" row — fed by EITHER the item-level
// stuck_items zone (PM, blocked issues) OR the project-RED fallback (the
// high-level at-risk rollup used for CLIENT / partner-in-context, #1322).
interface StuckRow {
  key:      string;
  to:       string;
  typeTag?: string;   // issue type (item-level only; absent for the project rollup)
  label:    string;   // issue title OR project name
  meta:     string;   // stuck-since (item) OR "N blocked" / "At risk" (project)
}

// DASH.B2: item-level when the zone has blocked issues; otherwise the B1
// project-level rollup (preserved as a fallback so CLIENT — for whom the zone
// is server-stripped — and empty-stuck PMs still see the at-risk signal).
function StuckItemsCard({ rows }: { rows: StuckRow[] }) {
  return (
    <CardShell testid="card-stuck-items" title="Needs attention" count={rows.length} accent="var(--error)">
      {rows.slice(0, 5).map((r) => (
        <Link key={r.key} to={r.to} style={rowLinkStyle}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.typeTag && <span data-testid="stuck-type" style={typeTagStyle}>{r.typeTag}</span>}
            {r.label}
          </span>
          <span style={{ color: 'var(--error)', fontSize: 12, flexShrink: 0 }} data-testid="stuck-since">
            {r.meta}
          </span>
        </Link>
      ))}
    </CardShell>
  );
}

function NotificationsPreviewCard({ items, unread }: { items: NotificationSummaryItem[]; unread: number }) {
  return (
    <CardShell
      testid="card-notifications"
      title="Notifications"
      count={unread}
      accent="var(--accent)"
      footer={
        NOTIFICATIONS_PAGE_PATH ? (
          <Link
            to={NOTIFICATIONS_PAGE_PATH}
            data-testid="notifications-view-all"
            style={{ color: 'var(--primary)', fontSize: 13, fontWeight: 500, textDecoration: 'none' }}
          >
            View all →
          </Link>
        ) : undefined
      }
    >
      {items.slice(0, 5).map((n) => (
        <div key={n.id} style={{ ...rowLinkStyle, cursor: 'default' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: n.isRead ? 400 : 600 }}>
            {n.title}
          </span>
          <span style={{ color: 'var(--text-secondary)', fontSize: 12, flexShrink: 0 }}>{formatTimeAgo(n.createdAt)}</span>
        </div>
      ))}
    </CardShell>
  );
}

// DASH.B2: real per-user assigned work from /overview, with the S8 timing
// badge reused from StoryStatusCard. Each row links to the owning project.
function MyAssignedWorkCard({ items }: { items: AssignedWorkItem[] }) {
  return (
    <CardShell testid="card-my-work" title="My assigned work" count={items.length}>
      {items.slice(0, 5).map((item) => (
        <Link key={item.id} to={`/projects/${item.projectId}`} style={rowLinkStyle}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
          {/* AssignedTiming is a 3-value subset of the badge's TimingStatus
              (the server collapses COMPLETED_* away), so this is type-safe.
              timing/daysLate are optional on AssignedWorkItem only because
              #2762's other three sourceTypes don't have them — every item
              this card renders is sourceType 'ISSUE', which always sets both. */}
          <TimingBadge timing={item.timing ?? 'ON_TRACK'} daysLate={item.daysLate ?? 0} />
        </Link>
      ))}
    </CardShell>
  );
}

// Static launch shortcuts (§5.3). Always renders for roles that include it
// (never empty). Surfaces the PKG "coming soon" affordance. Role-specific
// shortcuts (e.g. a cross-project sprint board) are a DASH.B2 concern — there's
// no project context at the dashboard level to target a board route, so a
// "Sprint board" link here would mislead.
function QuickLaunchCard() {
  const links: Array<{ to: string; label: string }> = [
    { to: '/projects', label: 'Projects' },
    { to: '/artefacts', label: 'Artefacts' },
  ];

  return (
    <CardShell testid="card-quick-launch" title="Quick launch">
      {links.map((l, i) => (
        <Link key={`${l.to}-${i}`} to={l.to} style={rowLinkStyle}>
          <span>{l.label}</span>
          <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Open →</span>
        </Link>
      ))}
      {/* PKG / pipeline (pipeline_status:null in /overview) — not yet available.
          Plain informational row: not a link and not a fake disabled control
          (aria-disabled has no effect on a non-interactive element). */}
      <div style={{ ...rowLinkStyle, color: 'var(--text-secondary)' }}>
        <span>Package Knowledge Graph</span>
        <span data-testid="quicklaunch-coming-soon" style={{ fontSize: 11, fontWeight: 600 }}>
          Coming soon
        </span>
      </div>
    </CardShell>
  );
}

// ── Zone container ───────────────────────────────────────────────────────────

export interface DashboardZone2ActionProps {
  cards: Zone2CardId[];
  overview: DashboardOverview | null;
  loading: boolean;
}

export default function DashboardZone2Action({ cards, overview, loading }: DashboardZone2ActionProps) {
  if (loading && !overview) {
    return (
      <div data-testid="dashboard-zone2" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {cards.map((id) => (
          <div
            key={id}
            data-testid="card-skeleton"
            className="animate-pulse"
            style={{ ...cardStyle, height: 120 }}
            aria-hidden="true"
          >
            <div style={{ height: 16, width: '40%', background: 'var(--surface-alt)', borderRadius: 4 }} />
            <div style={{ height: 12, width: '70%', background: 'var(--surface-alt)', borderRadius: 4 }} />
          </div>
        ))}
      </div>
    );
  }

  const ov = overview as DashboardOverview;
  const gates: UpcomingGate[] = ov?.upcoming_gates.data ?? [];
  const notifications = ov?.notification_summary.data?.last5 ?? [];
  const unread = ov?.notification_summary.data?.unreadCount ?? 0;
  const myWork: AssignedWorkItem[] = ov?.my_assigned_work.data ?? [];

  // StuckItems source: prefer the item-level blocked-issue zone (PM); fall back
  // to the project-level RED rollup when the zone is empty — CLIENT (the zone is
  // server-stripped) or a PM with no blocked issues — so the at-risk signal and
  // the partner-in-context client view (#1322) are preserved.
  const stuckItems: StuckItem[] = ov?.stuck_items.data ?? [];
  const stuckRows: StuckRow[] = stuckItems.length > 0
    ? stuckItems.map((it) => ({
        key:     it.id,
        to:      `/projects/${it.projectId}`,
        typeTag: it.type,
        label:   it.title,
        meta:    formatTimeAgo(it.stuckSince),
      }))
    : (ov?.active_projects.data ?? [])
        .filter((p) => p.healthStatus === 'RED')
        .map((p) => ({
          key:   p.id,
          to:    `/projects/${p.id}`,
          label: p.name,
          meta:  (p.blockedCount ?? 0) > 0 ? `${p.blockedCount} blocked` : 'At risk',
        }));

  const blocks: ReactNode[] = [];
  for (const id of cards) {
    switch (id) {
      case 'PendingApprovals':
        if (gates.length > 0) blocks.push(<PendingApprovalsCard key={id} items={gates} />);
        break;
      case 'StuckItems':
        if (stuckRows.length > 0) blocks.push(<StuckItemsCard key={id} rows={stuckRows} />);
        break;
      case 'NotificationsPreview':
        if (notifications.length > 0) blocks.push(<NotificationsPreviewCard key={id} items={notifications} unread={unread} />);
        break;
      case 'MyAssignedWork':
        if (myWork.length > 0) blocks.push(<MyAssignedWorkCard key={id} items={myWork} />);
        break;
      case 'QuickLaunch':
        blocks.push(<QuickLaunchCard key={id} />);
        break;
    }
  }

  if (blocks.length === 0) {
    return (
      <div
        data-testid="zone2-empty"
        style={{
          padding: '48px 24px',
          textAlign: 'center',
          borderRadius: 'var(--radius-lg)',
          border: '1px dashed var(--border)',
          background: 'var(--surface)',
          color: 'var(--text-secondary)',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>You&rsquo;re all caught up</div>
        <div style={{ fontSize: 13 }}>No pending actions right now.</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-zone2" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {blocks}
    </div>
  );
}
